/**
 * Account cache persistence for cross-platform authentication
 *
 * MSAL token caches don't always persist account info reliably between processes.
 * This module saves/loads account info so the MCP server can use it for silent auth.
 * Works on Windows, macOS, and Linux.
 */

import { existsSync, readFileSync, writeFileSync, mkdirSync, unlinkSync, chmodSync } from "node:fs";
import { join, dirname } from "node:path";
import type { AccountInfo } from "@azure/msal-node";
import { getConfigDir } from "../config/loader.js";
import { ACCOUNT_CACHE_FILE_NAME } from "../utils/constants.js";
import { logger } from "../utils/logger.js";

/**
 * Serializable account info (subset of MSAL AccountInfo)
 */
interface SerializedAccount {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  name?: string;
  nativeAccountId?: string;  // Used for silent auth across processes
}

/**
 * Get the account cache file path
 */
function getAccountCachePath(): string {
  return join(getConfigDir(), ACCOUNT_CACHE_FILE_NAME);
}

/**
 * Save account info to disk after successful authentication
 */
export function saveAccount(account: AccountInfo): void {
  const cachePath = getAccountCachePath();
  const cacheDir = dirname(cachePath);

  if (!existsSync(cacheDir)) {
    mkdirSync(cacheDir, { recursive: true });
  }

  const serialized: SerializedAccount = {
    homeAccountId: account.homeAccountId,
    environment: account.environment,
    tenantId: account.tenantId,
    username: account.username,
    localAccountId: account.localAccountId,
    name: account.name,
    nativeAccountId: account.nativeAccountId,
  };

  try {
    writeFileSync(cachePath, JSON.stringify(serialized, null, 2), {
      encoding: "utf-8",
      mode: 0o600,  // Owner read/write only - prevents other users from reading
    });
    // Ensure permissions on existing file (in case it was created with wrong perms)
    if (process.platform !== "win32") {
      try {
        chmodSync(cachePath, 0o600);
      } catch (chmodErr) {
        logger.debug({ err: chmodErr instanceof Error ? chmodErr.message : String(chmodErr) }, "Failed to chmod account cache (best effort)");
      }
    }
    logger.debug({ username: account.username }, "Saved account to cache");
  } catch (err) {
    logger.warn({ err, cachePath }, "Failed to save account cache");
  }
}

/**
 * Load account info from disk
 * Returns null if no cached account or if it fails to load
 */
export function loadAccount(): AccountInfo | null {
  const cachePath = getAccountCachePath();

  if (!existsSync(cachePath)) {
    logger.debug("No account cache file found");
    return null;
  }

  try {
    const content = readFileSync(cachePath, "utf-8");
    const parsed = JSON.parse(content) as SerializedAccount;

    // Block prototype pollution via crafted JSON
    if (
      Object.prototype.hasOwnProperty.call(parsed, "__proto__") ||
      Object.prototype.hasOwnProperty.call(parsed, "constructor") ||
      Object.prototype.hasOwnProperty.call(parsed, "prototype")
    ) {
      logger.warn("Account cache contains dangerous prototype keys — rejecting");
      return null;
    }

    // Validate required fields exist and are strings (defense against cache tampering)
    if (
      typeof parsed.homeAccountId !== "string" || !parsed.homeAccountId ||
      typeof parsed.environment !== "string" || !parsed.environment ||
      typeof parsed.tenantId !== "string" || !parsed.tenantId ||
      typeof parsed.username !== "string" || !parsed.username ||
      typeof parsed.localAccountId !== "string" || !parsed.localAccountId
    ) {
      logger.warn("Account cache file has missing or invalid fields");
      return null;
    }

    // Validate optional fields are correct type if present
    if (parsed.name !== undefined && typeof parsed.name !== "string") {
      logger.warn("Account cache has invalid name field");
      return null;
    }
    if (parsed.nativeAccountId !== undefined && typeof parsed.nativeAccountId !== "string") {
      logger.warn("Account cache has invalid nativeAccountId field");
      return null;
    }

    // Convert back to AccountInfo format
    const account: AccountInfo = {
      homeAccountId: parsed.homeAccountId,
      environment: parsed.environment,
      tenantId: parsed.tenantId,
      username: parsed.username,
      localAccountId: parsed.localAccountId,
      name: parsed.name,
      idTokenClaims: undefined,
      nativeAccountId: parsed.nativeAccountId,
      authorityType: "MSSTS",
    };

    logger.debug({ username: account.username }, "Loaded account from cache");
    return account;
  } catch (err) {
    logger.warn({ err, cachePath }, "Failed to load account cache");
    return null;
  }
}

/**
 * Clear the cached account (for logout)
 * Deletes the file instead of writing empty content for security
 */
export function clearAccountCache(): void {
  const cachePath = getAccountCachePath();

  if (existsSync(cachePath)) {
    try {
      unlinkSync(cachePath);
      logger.debug("Deleted account cache file");
    } catch (err) {
      logger.warn({ err: err instanceof Error ? err.message : String(err) }, "Failed to delete account cache, trying overwrite fallback");
      // If deletion fails, try to overwrite with empty content as fallback
      try {
        writeFileSync(cachePath, "{}", { encoding: "utf-8", mode: 0o600 });
        logger.debug("Cleared account cache (fallback)");
      } catch (innerErr) {
        logger.warn({ err: innerErr instanceof Error ? innerErr.message : String(innerErr) }, "Failed to clear account cache");
      }
    }
  }
}
